import {useState, useEffect, useCallback} from 'react'
import {useQueryClient} from '@tanstack/react-query'
import Modal from '@/customComponents/uiComponents/modal/Modal.jsx'
import ModalHeader from '@/customComponents/uiComponents/modal/ModalHeader.jsx'
import {RadioButton} from '@/customComponents/uiComponents/RadioButton.jsx'
import {happyRide, unhappyRide, nightmareRide, greenTick} from '@img/index.js'
import {useFeedbackQuestions} from '@/hooks/useFeedback'
import {feedbackService} from '@/services/feedbackService'

function ratingForType(type) {
    if (type === 'happy') return 5
    if (type === 'unhappy') return 3
    if (type === 'nightmare') return 1
    return 5
}

function prefillForType(type) {
    if (type === 'happy') return 'Everything was great! The driver was on time, friendly and the ride was smooth.'
    if (type === 'unhappy') return 'The ride was okay but a few things could have been better.'
    if (type === 'nightmare') return "I had a bad experience during this ride. Here's what went wrong:"
    return ''
}

function iconForType(type) {
    if (type === 'happy') return happyRide
    if (type === 'unhappy') return unhappyRide
    if (type === 'nightmare') return nightmareRide
    return happyRide
}

export default function ReviewRidePopup({isOpen, onClose, tripId, initialType}) {
    const queryClient = useQueryClient()
    const {data: qData, isLoading: questionsLoading} = useFeedbackQuestions()
    const questions = qData?.questions || []

    const [selectedType, setSelectedType] = useState(initialType || 'happy')
    const [comment, setComment] = useState(prefillForType(initialType || 'happy'))
    const [answers, setAnswers] = useState({})
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [submitted, setSubmitted] = useState(false)
    const [error, setError] = useState('')

    useEffect(() => {
        if (!isOpen || !initialType) return
        setSelectedType(initialType)
        setComment(prefillForType(initialType))
        setAnswers({})
        setSubmitted(false)
        setError('')
    }, [initialType, isOpen])

    const handleSelectType = (type) => {
        setSelectedType(type)
        setComment(prefillForType(type))
    }

    const handleClose = useCallback(() => {
        onClose?.()
        setTimeout(() => {
            setAnswers({})
            setError('')
            setIsSubmitting(false)
            setSubmitted(false)
        }, 250)
    }, [onClose])

    const handleSubmit = async () => {
        setError('')
        setIsSubmitting(true)
        // The API takes one feedback string (no structured answers) — compose it
        // from the comment plus the answered questions, one per line.
        const answerLines = questions
            .filter(q => answers[q.id] && String(answers[q.id]).trim())
            .map(q => {
                const value = String(answers[q.id]).trim()
                return `${q.question} ${q.answer_type === 'Stars' ? `${value}/5` : value}`
            })
        const feedbackText = [comment?.trim(), ...answerLines].filter(Boolean).join('\n').slice(0, 2000)

        const result = await feedbackService.submitTripFeedback(tripId, {
            rating: ratingForType(selectedType),
            feedback: feedbackText || null,
        })
        setIsSubmitting(false)

        if (!result.success) {
            setError(result.error || 'Could not submit your feedback. Please try again.')
            return
        }

        queryClient.invalidateQueries({queryKey: ['tripFeedback', tripId]})
        setSubmitted(true)
    }

    return (
        <Modal isOpen={isOpen} onClose={handleClose}
               footer={!submitted && (
                   <button type="button" onClick={handleSubmit} disabled={isSubmitting}
                           className="hopn-add-passenger-button">
                       {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
                   </button>
               )}>
            <ModalHeader title={submitted ? '' : 'Rate your ride'} onClose={handleClose}/>

            {submitted ? (
                <div className="hopn-view-in flex flex-col items-center justify-center py-8 text-center">
                    <img src={greenTick} alt="" className="w-16 h-16 mb-4"/>
                    <h3 className="font-bold mb-3!">Thanks for your feedback!</h3>
                    <p className="text-(--text-secondary) max-w-100 mb-6!">Your response helps us and our drivers keep improving every ride.</p>
                    <button type="button" onClick={handleClose}
                            className="cursor-pointer py-3 px-12 rounded-full bg-(--text-primary) text-white font-bold border-0">
                        Done
                    </button>
                </div>
            ) : (
                <div className="hopn-view-in">
                    <div className="flex flex-col items-center mb-6">
                        <img src={iconForType(selectedType)} alt="" className="h-24 mb-4 rounded-2xl"/>
                        <div className="flex items-center gap-4">
                            <button type="button" onClick={() => handleSelectType('happy')}
                                    className={`cursor-pointer bg-transparent overflow-hidden rounded-xl transition ${selectedType === 'happy' ? 'border-2 border-(--accent-green)' : 'border-2 border-transparent opacity-60'}`}>
                                <img src={happyRide} alt="Happy" className="h-12"/>
                            </button>
                            <button type="button" onClick={() => handleSelectType('unhappy')}
                                    className={`cursor-pointer bg-transparent overflow-hidden rounded-xl transition ${selectedType === 'unhappy' ? 'border-2 border-(--accent-green)' : 'border-2 border-transparent opacity-60'}`}>
                                <img src={unhappyRide} alt="Unhappy" className="h-12"/>
                            </button>
                            <button type="button" onClick={() => handleSelectType('nightmare')}
                                    className={`cursor-pointer bg-transparent overflow-hidden rounded-xl transition ${selectedType === 'nightmare' ? 'border-2 border-(--accent-green)' : 'border-2 border-transparent opacity-60'}`}>
                                <img src={nightmareRide} alt="Nightmare" className="h-12"/>
                            </button>
                        </div>
                    </div>

                    <textarea value={comment} onChange={e => setComment(e.target.value)} rows={4}
                              placeholder="Tell us more about your ride"
                              className="w-full! border border-(--border-color) rounded-(--radius-2xl) p-5 text-base outline-none resize-none mb-5"/>

                    {questionsLoading ? (
                        <div className="animate-pulse space-y-3 mb-5">
                            <div className="h-4 bg-gray-200 rounded w-2/3"/>
                            <div className="h-20 bg-gray-200 rounded-2xl"/>
                            <div className="h-4 bg-gray-200 rounded w-2/3"/>
                            <div className="h-20 bg-gray-200 rounded-2xl"/>
                        </div>
                    ) : questions.length > 0 && (
                        <div className="flex flex-col gap-4 mb-5">
                            {questions.map(q => (
                                <div key={q.id}>
                                    <label className="block font-medium mb-2">{q.question}</label>
                                    {q.answer_type === 'YesNo' ? (
                                        <div className="flex items-center gap-6">
                                            {['Yes', 'No'].map(opt => (
                                                <label key={opt} className="flex items-center gap-2 cursor-pointer">
                                                    <RadioButton name={`q-${q.id}`} value={opt}
                                                                 checked={answers[q.id] === opt}
                                                                 onChange={() => setAnswers(a => ({...a, [q.id]: opt}))}/>
                                                    {opt}
                                                </label>
                                            ))}
                                        </div>
                                    ) : q.answer_type === 'Stars' ? (
                                        <div className="flex items-center gap-1">
                                            {[1, 2, 3, 4, 5].map(n => {
                                                const active = Number(answers[q.id] || 0) >= n
                                                return (
                                                    <button key={n} type="button" onClick={() => setAnswers(a => ({...a, [q.id]: String(n)}))}
                                                            className={`cursor-pointer bg-transparent border-0 p-0 text-3xl leading-none ${active ? 'text-yellow-400' : 'text-gray-300'}`}>
                                                        ★
                                                    </button>
                                                )
                                            })}
                                        </div>
                                    ) : (
                                        <textarea value={answers[q.id] || ''} onChange={e => setAnswers(a => ({...a, [q.id]: e.target.value}))} rows={2}
                                                  placeholder={q.answer_type_label || 'Your answer'}
                                                  className="w-full! border border-(--border-color) rounded-(--radius-2xl) p-4 text-base outline-none resize-none"/>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}

                    {error && <p className="text-(--text-red) mb-3!">{error}</p>}
                </div>
            )}
        </Modal>
    )
}
